import { useEffect, useState } from "react";
import { FileText, Send, Download, Trash2, Search, AlertCircle, Clock, RefreshCw } from "lucide-react";
import { api, API } from "../../lib/api";
import { toast } from "sonner";

const STATO = {
  generata:        { cls: "bg-amber-500/15 text-amber-300 border-amber-500/30",     icon: Clock,         label: "Generata" },
  inviata:         { cls: "bg-sky-500/15 text-sky-300 border-sky-500/30",           icon: Send,          label: "Inviata" },
  errore:          { cls: "bg-rose-500/15 text-rose-300 border-rose-500/30",        icon: AlertCircle,   label: "Errore" },
};

const SDI_STATO = {
  in_attesa:       { cls: "bg-amber-500/15 text-amber-300",  label: "In attesa SDI" },
  inviata:         { cls: "bg-sky-500/15 text-sky-300",      label: "Inviata SDI" },
  consegnata:      { cls: "bg-emerald-500/15 text-emerald-300", label: "Consegnata ✓" },
  accettata:       { cls: "bg-emerald-500/20 text-emerald-200", label: "Accettata PA ✓" },
  scartata:        { cls: "bg-rose-500/20 text-rose-300",    label: "Scartata SDI" },
  non_consegnata:  { cls: "bg-orange-500/15 text-orange-300", label: "Non consegnata" },
  errore_polling:  { cls: "bg-white/[0.05] text-white/40",   label: "—" },
};

export default function Fatture() {
  const [list, setList] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [search, setSearch] = useState("");
  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState({ contract_id: "", aliquota_iva: 22, invia: false, causale: "", descrizione_riga: "" });
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = async () => {
    const [f, c] = await Promise.all([api.get("/fatture"), api.get("/contracts")]);
    setList(f.data || []);
    setContracts(c.data || []);
  };
  useEffect(() => { load(); }, []);

  // Auto-refresh SDI ogni 5 minuti per fatture inviate non finalizzate
  useEffect(() => {
    const hasPending = list.some((f) => f.stato === 'inviata' && !['consegnata','accettata','scartata'].includes(f.sdi_status));
    if (!hasPending) return;
    const t = setInterval(() => refreshAllSdi(true), 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [list]);

  const refreshAllSdi = async (silent = false) => {
    setRefreshing(true);
    try {
      const r = await api.post("/fatture/refresh-all-sdi");
      setLastRefresh(new Date());
      if (!silent) {
        if (r.data.totali === 0) toast.info("Nessuna fattura da aggiornare");
        else toast.success(`Stato SDI aggiornato: ${r.data.aggiornate}/${r.data.totali}`);
      }
      load();
    } catch (e) {
      if (!silent) toast.error("Errore refresh SDI: " + (e?.response?.data?.detail || e.message));
    } finally { setRefreshing(false); }
  };

  const refreshOneSdi = async (fid) => {
    try {
      const r = await api.post(`/fatture/${fid}/refresh-sdi`);
      toast.success(`Stato: ${r.data.sdi_status_label || r.data.sdi_status}`);
      load();
    } catch (e) {
      toast.error("Errore: " + (e?.response?.data?.detail || e.message));
    }
  };

  const generate = async (e) => {
    e.preventDefault();
    if (!genForm.contract_id) { toast.error("Seleziona un contratto"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/contracts/${genForm.contract_id}/fattura-elettronica`, {
        invia: genForm.invia,
        aliquota_iva: +genForm.aliquota_iva,
        causale: genForm.causale || undefined,
        descrizione_riga: genForm.descrizione_riga || undefined,
      });
      toast.success(`Fattura ${r.data.numero} ${genForm.invia ? 'generata e inviata' : 'generata'} ✓`);
      setGenOpen(false);
      setGenForm({ contract_id: "", aliquota_iva: 22, invia: false, causale: "", descrizione_riga: "" });
      load();
    } catch (e) {
      toast.error("Errore: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const inviaSDI = async (fid) => {
    if (!window.confirm("Inviare definitivamente questa fattura al Sistema di Interscambio (SDI) tramite Aruba?")) return;
    try {
      const r = await api.post(`/fatture/${fid}/invia`);
      toast.success("Fattura trasmessa a SDI tramite Aruba ✓");
      load();
    } catch (e) {
      toast.error("Errore invio: " + (e?.response?.data?.detail || e.message));
    }
  };

  const downloadXml = (fid, numero) => {
    fetch(`${API}/fatture/${fid}/xml`, { credentials: 'include' })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = `${numero}.xml`; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const del = async (fid) => {
    if (!window.confirm("Eliminare la fattura? (solo se non già inviata)")) return;
    try {
      await api.delete(`/fatture/${fid}`);
      toast.success("Eliminata");
      load();
    } catch (e) {
      toast.error("Errore: " + (e?.response?.data?.detail || e.message));
    }
  };

  const filtered = list.filter((f) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [f.numero, f.causale, f.customer?.ragione_sociale, f.customer?.nome, f.customer?.cognome].some((x) => x?.toLowerCase().includes(s));
  });

  const totals = list.reduce((a, f) => ({
    imponibile: a.imponibile + (f.imponibile || 0),
    iva: a.iva + (f.iva || 0),
    totale: a.totale + (f.totale || 0),
    inviate: a.inviate + (f.stato === 'inviata' ? 1 : 0),
  }), { imponibile: 0, iva: 0, totale: 0, inviate: 0 });

  return (
    <div className="space-y-6" data-testid="fatture-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">Fatturazione Elettronica</h1>
          <p className="text-sm text-white/45 mt-1">
            {list.length} fatture · {totals.inviate} inviate a SDI · XML FatturaPA v1.2.2
          </p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input placeholder="Cerca numero, cliente…" value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="fatture-search"
              className="bg-white/[0.03] border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none w-72" />
          </div>
          <button data-testid="fatture-refresh-sdi" disabled={refreshing} onClick={() => refreshAllSdi(false)}
            title={lastRefresh ? `Ultimo check: ${lastRefresh.toLocaleTimeString('it-IT')}` : 'Forza check stato SDI'}
            className="px-4 py-2 rounded-full text-xs inline-flex items-center gap-2 bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] disabled:opacity-50">
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Aggiorna SDI
          </button>
          <button data-testid="fattura-genera-btn" onClick={() => setGenOpen(true)} className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2">
            <FileText size={14} /> Genera Fattura
          </button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="glass rounded-2xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Totale Imponibile</div>
          <div className="font-display text-2xl font-bold mt-1">€{totals.imponibile.toFixed(2)}</div>
        </div>
        <div className="glass rounded-2xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Totale IVA</div>
          <div className="font-display text-2xl font-bold mt-1">€{totals.iva.toFixed(2)}</div>
        </div>
        <div className="glass rounded-2xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Totale Fatturato</div>
          <div className="font-display text-2xl font-bold mt-1 neon-text">€{totals.totale.toFixed(2)}</div>
        </div>
        <div className="glass rounded-2xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Inviate a SDI</div>
          <div className="font-display text-2xl font-bold mt-1 text-emerald-300">{totals.inviate}/{list.length}</div>
        </div>
      </div>

      <div className="glass rounded-2xl overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-white/40">
            <tr>
              <th className="text-left p-4">Numero</th>
              <th className="text-left p-4">Data</th>
              <th className="text-left p-4">Cliente</th>
              <th className="text-right p-4">Imponibile</th>
              <th className="text-right p-4">IVA</th>
              <th className="text-right p-4">Totale</th>
              <th className="p-4 text-center">Stato SDI</th>
              <th className="p-4 text-center">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filtered.map((f) => {
              const StatoInfo = STATO[f.stato] || STATO.generata;
              const StatoIcon = StatoInfo.icon;
              const cliente = f.customer?.ragione_sociale || `${f.customer?.nome || ''} ${f.customer?.cognome || ''}`.trim() || '—';
              return (
                <tr key={f.id} className="hover:bg-white/[0.02]" data-testid={`fattura-${f.numero}`}>
                  <td className="p-4 font-mono text-xs font-bold text-[#ff0033]">{f.numero}</td>
                  <td className="p-4 text-xs font-mono">{f.data}</td>
                  <td className="p-4">
                    <div className="text-sm">{cliente}</div>
                    <div className="text-[10px] text-white/40 font-mono">P.IVA {f.customer?.piva || f.customer?.codice_fiscale || '—'}</div>
                  </td>
                  <td className="p-4 text-right font-mono text-xs">€{(f.imponibile || 0).toFixed(2)}</td>
                  <td className="p-4 text-right font-mono text-xs text-white/60">€{(f.iva || 0).toFixed(2)}</td>
                  <td className="p-4 text-right font-display font-bold neon-text">€{(f.totale || 0).toFixed(2)}</td>
                  <td className="p-4 text-center">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border inline-flex items-center gap-1 ${StatoInfo.cls}`}>
                      <StatoIcon size={10} /> {StatoInfo.label}
                    </span>
                    {f.stato === 'inviata' && (() => {
                      const sdi = SDI_STATO[f.sdi_status] || SDI_STATO.in_attesa;
                      return (
                        <div className={`mt-1 inline-block text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${sdi.cls}`}>
                          {f.sdi_status_label || sdi.label}
                        </div>
                      );
                    })()}
                    {f.sdi_last_check && (
                      <div className="text-[8px] text-white/25 font-mono mt-0.5">check {f.sdi_last_check.slice(11,16)}</div>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-center gap-1.5">
                      <button title="Scarica XML" onClick={() => downloadXml(f.id, f.numero)}
                        className="p-1.5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-white/60">
                        <Download size={12} />
                      </button>
                      {f.stato === 'inviata' && (
                        <button title="Aggiorna stato SDI" onClick={() => refreshOneSdi(f.id)}
                          data-testid={`fattura-refresh-${f.numero}`}
                          className="p-1.5 rounded-lg bg-white/[0.04] hover:bg-sky-500/20 text-white/60 hover:text-sky-300">
                          <RefreshCw size={12} />
                        </button>
                      )}
                      {f.stato !== 'inviata' && (
                        <button title="Invia a SDI" onClick={() => inviaSDI(f.id)}
                          data-testid={`fattura-invia-${f.numero}`}
                          className="p-1.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 inline-flex items-center gap-1">
                          <Send size={12} /> <span className="text-[10px]">SDI</span>
                        </button>
                      )}
                      {f.stato !== 'inviata' && (
                        <button title="Elimina" onClick={() => del(f.id)}
                          className="p-1.5 rounded-lg bg-white/[0.04] hover:bg-rose-500/20 text-white/60 hover:text-rose-300">
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan="8" className="p-10 text-center text-white/40">Nessuna fattura — genera la prima dal contratto.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* GENERA FATTURA MODAL */}
      {genOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={() => !busy && setGenOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={generate} className="w-full max-w-2xl glass-strong rounded-3xl p-6 max-h-[92vh] overflow-auto" data-testid="fattura-form">
            <div className="font-display text-2xl font-bold mb-1">Genera Fattura Elettronica</div>
            <div className="text-xs text-white/45 mb-5">XML FatturaPA v1.2.2 conforme SDI · invio tramite Aruba</div>

            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Contratto</label>
                <select required value={genForm.contract_id} onChange={(e) => setGenForm({ ...genForm, contract_id: e.target.value })}
                  data-testid="fattura-contract"
                  className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="">— Seleziona —</option>
                  {contracts.map((c) => {
                    const cli = c.customer?.ragione_sociale || `${c.customer?.nome || ''} ${c.customer?.cognome || ''}`.trim();
                    return <option key={c.id} value={c.id}>{c.numero} · {cli} · €{c.costo_totale?.toFixed(2)}</option>;
                  })}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/40">Aliquota IVA %</label>
                  <input type="number" step="0.01" value={genForm.aliquota_iva} onChange={(e) => setGenForm({ ...genForm, aliquota_iva: e.target.value })}
                    className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 w-full">
                    <input type="checkbox" checked={genForm.invia} onChange={(e) => setGenForm({ ...genForm, invia: e.target.checked })}
                      data-testid="fattura-invia-toggle"
                      className="accent-[#ff0033]" />
                    <span className="text-xs">Invia subito a SDI tramite Aruba</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Descrizione riga (override)</label>
                <input value={genForm.descrizione_riga} onChange={(e) => setGenForm({ ...genForm, descrizione_riga: e.target.value })}
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"
                  placeholder="Lascia vuoto per default: Noleggio + marca/modello/targa" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Causale</label>
                <input value={genForm.causale} onChange={(e) => setGenForm({ ...genForm, causale: e.target.value })}
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"
                  placeholder="Default automatico dal contratto" />
              </div>
            </div>

            <div className="flex gap-3 mt-6 justify-end">
              <button type="button" disabled={busy} onClick={() => setGenOpen(false)} className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10 disabled:opacity-50">Annulla</button>
              <button data-testid="fattura-genera-submit" disabled={busy} type="submit" className="btn-neon px-6 py-2.5 rounded-full text-sm">
                {busy ? "Generazione..." : (genForm.invia ? "Genera & Invia SDI" : "Genera XML")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
